import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { Link } from "react-router-dom";
import { useCart } from "../pages/js/CartContext";
import { API_BASE_URL, getPayPalClientId } from "../API/endpoint";
import "./Panier.css";

// Composant pour les boutons PayPal qui attend le chargement du SDK
function PayPalButtonWrapper({
  validateShippingForm,
  getTotal,
  getShippingCost,
  shippingAddress,
  shippingPhone,
  shippingEmail,
  shippingName,
  shippingLastName,
  cart,
  useDifferentBillingAddress,
  billingAddress,
  billingPhone,
  billingName,
  billingLastName,
  additionalInfo,
  clearCart,
  setShowSuccessNotification,
  shippingMethod,
}) {
  const [{ isPending }] = usePayPalScriptReducer();

  if (isPending) {
    return <div>Chargement de PayPal...</div>;
  }

  return (
    <PayPalButtons
      fundingSource={window.paypal?.FUNDING?.CARD}
      style={{ layout: "vertical" }}
      createOrder={(data, actions) => {
        if (!validateShippingForm()) {
          return Promise.reject(new Error("VALIDATION_ERROR"));
        }
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                value: (getTotal() + getShippingCost()).toFixed(2),
                currency_code: "EUR",
              },
              shipping: {
                address: {
                  address_line_1: shippingAddress.address_line_1,
                  address_line_2: shippingAddress.address_line_2,
                  admin_area_2: shippingAddress.admin_area_2,
                  postal_code: shippingAddress.postal_code,
                  country_code: shippingAddress.country_code,
                },
              },
            },
          ],
          application_context: {
            shipping_preference: "NO_SHIPPING",
          },
        });
      }}
      onApprove={async (data, actions) => {
        const details = await actions.order.capture();
        const payerInfo = {
          firstName: details.payer?.name?.given_name,
          lastName: details.payer?.name?.surname,
          email: details.payer?.email_address,
        };
        fetch(`${API_BASE_URL}/api/paypal/validate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderID: data.orderID,
            payerID: data.payerID,
            cart,
            total: getTotal(),
            shipping: getShippingCost(),
            shippingMethod,
            shippingAddress,
            shippingPhone,
            shippingEmail,
            shippingName,
            shippingLastName,
            billingAddress: useDifferentBillingAddress ? billingAddress : null,
            billingPhone: useDifferentBillingAddress ? billingPhone : null,
            billingName: useDifferentBillingAddress ? billingName : null,
            billingLastName: useDifferentBillingAddress ? billingLastName : null,
            payerInfo,
            additionalInfo,
          }),
        })
          .then((res) => res.json())
          .then((result) => {
            setShowSuccessNotification(true);
            setTimeout(() => {
              setShowSuccessNotification(false);
              clearCart();
            }, 3000);
          })
          .catch(() => alert("Erreur lors de la validation du paiement."));
      }}
      onError={(err) => {
        // Ne pas afficher d'erreur si c'est une erreur de validation
        if (err && !err.message?.includes("VALIDATION_ERROR")) {
          console.error("Erreur PayPal:", err);
        }
      }}
    />
  );
}

function Panier() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotal,
    getShippingCost,
    shippingMethod,
    setShippingMethod,
  } = useCart();
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [useDifferentBillingAddress, setUseDifferentBillingAddress] =
    useState(false);
  const [billingAddress, setBillingAddress] = useState({
    address_line_1: "",
    address_line_2: "",
    admin_area_2: "",
    postal_code: "",
    country_code: "FR",
  });
  const [billingPhone, setBillingPhone] = useState("");
  const [billingName, setBillingName] = useState("");
  const [billingLastName, setBillingLastName] = useState("");
  // Ajout de l'adresse de livraison
  const [shippingAddress, setShippingAddress] = useState({
    address_line_1: "",
    address_line_2: "",
    admin_area_2: "",
    postal_code: "",
    country_code: "FR",
  });
  const [shippingPhone, setShippingPhone] = useState("");
  const [shippingEmail, setShippingEmail] = useState("");
  const [shippingName, setShippingName] = useState("");
  const [shippingLastName, setShippingLastName] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // Charger le PayPal Client ID au montage du composant
  useEffect(() => {
    getPayPalClientId().then(clientId => {
      setPaypalClientId(clientId);
    }).catch(error => {
      console.error('Failed to load PayPal config:', error);
    });
  }, []);

  // Fonction de validation de l'email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction de validation du téléphone
  const validatePhone = (phone) => {
    const phoneRegex = /^(\+33|0)[1-9](\d{2}){4}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  // Fonction de validation du formulaire
  const validateShippingForm = () => {
    const errors = {};

    if (!shippingName.trim()) {
      errors.shippingName = "Le nom est obligatoire";
    }

    if (!shippingLastName.trim()) {
      errors.shippingLastName = "Le prénom est obligatoire";
    }

    if (!shippingEmail.trim()) {
      errors.shippingEmail = "L'email est obligatoire";
    } else if (!validateEmail(shippingEmail)) {
      errors.shippingEmail = "L'email n'est pas valide";
    }

    if (!shippingPhone.trim()) {
      errors.shippingPhone = "Le téléphone est obligatoire";
    } else if (!validatePhone(shippingPhone)) {
      errors.shippingPhone =
        "Le téléphone n'est pas valide (format: 0612345678 ou +33612345678)";
    }

    if (!shippingAddress.address_line_1.trim()) {
      errors.address_line_1 = "L'adresse est obligatoire";
    }

    if (!shippingAddress.postal_code.trim()) {
      errors.postal_code = "Le code postal est obligatoire";
    }

    if (!shippingAddress.admin_area_2.trim()) {
      errors.admin_area_2 = "La ville est obligatoire";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <main className="App-main magicpatern3">
      <Helmet>
        <title>Panier - Déco Ariège</title>
        <meta name="description" content="Votre panier d'achat pour les produits en bois artisanal. Procédez au paiement sécurisé avec PayPal." />
      </Helmet>
      {showSuccessNotification && (
        <div className="success-notification-overlay">
          <div className="success-notification">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Paiement validé !</span>
          </div>
        </div>
      )}
      <h1>Panier</h1>
      <Link to="/boutique" className="panier-back-link">
        ← Retour à la Boutique
      </Link>

      {cart.length === 0 ? (
        <div className="panier-section-white">
          <p>Votre panier est vide pour le moment.</p>
        </div>
      ) : (
        <div className="panier-container">
          <div className="panier-products-section">
            <table className="panier-table">
              <thead>
                <tr>
                  <th>PRODUIT</th>
                  <th>PRIX</th>
                  <th>QUANTITÉ</th>
                  <th>SOUS-TOTAL</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item) => (
                  <tr key={item.id}>
                    <td className="panier-product-info">
                      {item.path && (
                        <img
                          src={`${API_BASE_URL}${item.path}`}
                          alt={item.title || item.name}
                        />
                      )}
                      <div className="panier-product-details">
                        <span className="panier-product-name">
                          {item.title || item.name}
                        </span>
                        <p className="panier-product-description">
                          {item.description || "Réalisé à la main."}
                        </p>
                      </div>
                    </td>
                    <td>{parseFloat(item.price).toFixed(2)} €</td>
                    <td>
                      <div className="panier-quantity-controls">
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td>{(item.price * item.quantity).toFixed(2)} €</td>
                    <td>
                      <button
                        className="panier-remove-btn"
                        onClick={() => removeFromCart(item.id)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="panier-actions">
              <button
                className="panier-continue-btn"
                onClick={() => (window.location.href = "/boutique")}
              >
                ← POURSUIVRE LES ACHATS
              </button>
              <button className="panier-update-btn" onClick={() => clearCart()}>
                VIDER LE PANIER
              </button>
            </div>
          </div>
          <div className="panier-summary">
            <h2>TOTAL PANIER</h2>
            <div className="panier-summary-row">
              <span>Sous-total</span>
              <span>{getTotal().toFixed(2)} €</span>
            </div>
            <div className="panier-summary-row">
              <label>Méthode de livraison:</label>
              <select value={shippingMethod} onChange={(e) => setShippingMethod(e.target.value)} className="shipping-method-select">
                <option value="colissimo">Colissimo</option>
                <option value="pickup">Retrait en magasin (0€)</option>
              </select>
            </div>
            <div className="panier-summary-row">
              <span>Frais de livraison: </span>
              <span>{getShippingCost()} €</span>
            </div>
            <div className="panier-summary-section">
              <h3>Adresse de livraison</h3>
              <div className="shipping-address-form">
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Nom *"
                    value={shippingName}
                    onChange={(e) => {
                      setShippingName(e.target.value);
                      if (validationErrors.shippingName) {
                        setValidationErrors({
                          ...validationErrors,
                          shippingName: null,
                        });
                      }
                    }}
                    className={`form-input ${validationErrors.shippingName ? 'error' : ''}`}
                    required
                  />
                  {validationErrors.shippingName && (
                    <span className="validation-error-message">
                      {validationErrors.shippingName}
                    </span>
                  )}
                </div>
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Prénom *"
                    value={shippingLastName}
                    onChange={(e) => {
                      setShippingLastName(e.target.value);
                      if (validationErrors.shippingLastName) {
                        setValidationErrors({
                          ...validationErrors,
                          shippingLastName: null,
                        });
                      }
                    }}
                    className={`form-input ${validationErrors.shippingLastName ? 'error' : ''}`}
                    required
                  />
                  {validationErrors.shippingLastName && (
                    <span className="validation-error-message">
                      {validationErrors.shippingLastName}
                    </span>
                  )}
                </div>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Email *"
                    value={shippingEmail}
                    onChange={(e) => {
                      setShippingEmail(e.target.value);
                      if (validationErrors.shippingEmail) {
                        setValidationErrors({
                          ...validationErrors,
                          shippingEmail: null,
                        });
                      }
                    }}
                    className={`form-input ${validationErrors.shippingEmail ? 'error' : ''}`}
                    required
                  />
                  {validationErrors.shippingEmail && (
                    <span className="validation-error-message">
                      {validationErrors.shippingEmail}
                    </span>
                  )}
                </div>
                <div className="form-field">
                  <input
                    type="tel"
                    placeholder="Téléphone * (ex: 0612345678)"
                    value={shippingPhone}
                    onChange={(e) => {
                      setShippingPhone(e.target.value);
                      if (validationErrors.shippingPhone) {
                        setValidationErrors({
                          ...validationErrors,
                          shippingPhone: null,
                        });
                      }
                    }}
                    className={`form-input ${validationErrors.shippingPhone ? 'error' : ''}`}
                    required
                  />
                  {validationErrors.shippingPhone && (
                    <span className="validation-error-message">
                      {validationErrors.shippingPhone}
                    </span>
                  )}
                </div>
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Adresse ligne 1 *"
                    value={shippingAddress.address_line_1}
                    onChange={(e) => {
                      setShippingAddress({
                        ...shippingAddress,
                        address_line_1: e.target.value,
                      });
                      if (validationErrors.address_line_1) {
                        setValidationErrors({
                          ...validationErrors,
                          address_line_1: null,
                        });
                      }
                    }}
                    className={`form-input ${validationErrors.address_line_1 ? 'error' : ''}`}
                    required
                  />
                  {validationErrors.address_line_1 && (
                    <span className="validation-error-message">
                      {validationErrors.address_line_1}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Adresse ligne 2 (optionnel)"
                  value={shippingAddress.address_line_2}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      address_line_2: e.target.value,
                    })
                  }
                  className="form-input"
                />
                <div className="form-row">
                  <div className="form-field flex-1">
                    <input
                      type="text"
                      placeholder="Code postal *"
                      value={shippingAddress.postal_code}
                      onChange={(e) => {
                        setShippingAddress({
                          ...shippingAddress,
                          postal_code: e.target.value,
                        });
                        if (validationErrors.postal_code) {
                          setValidationErrors({
                            ...validationErrors,
                            postal_code: null,
                          });
                        }
                      }}
                      className={`form-input ${validationErrors.postal_code ? 'error' : ''}`}
                      required
                    />
                    {validationErrors.postal_code && (
                      <span className="validation-error-message">
                        {validationErrors.postal_code}
                      </span>
                    )}
                  </div>
                  <div className="form-field flex-2">
                    <input
                      type="text"
                      placeholder="Ville *"
                      value={shippingAddress.admin_area_2}
                      onChange={(e) => {
                        setShippingAddress({
                          ...shippingAddress,
                          admin_area_2: e.target.value,
                        });
                        if (validationErrors.admin_area_2) {
                          setValidationErrors({
                            ...validationErrors,
                            admin_area_2: null,
                          });
                        }
                      }}
                      className={`form-input ${validationErrors.admin_area_2 ? 'error' : ''}`}
                      required
                    />
                    {validationErrors.admin_area_2 && (
                      <span className="validation-error-message">
                        {validationErrors.admin_area_2}
                      </span>
                    )}
                  </div>
                </div>
                <select
                  value={shippingAddress.country_code}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      country_code: e.target.value,
                    })
                  }
                  className="form-input"
                  required
                >
                  <option value="FR">France</option>
                  <option value="BE">Belgique</option>
                  <option value="CH">Suisse</option>
                  <option value="LU">Luxembourg</option>
                  <option value="ES">Espagne</option>
                  <option value="IT">Italie</option>
                  <option value="DE">Allemagne</option>
                </select>
              </div>
            </div>
            <div className="panier-summary-section">
              <h3>Informations supplémentaires</h3>
              <textarea
                className="panier-additional-info"
                placeholder="Ajoutez ici toute information supplémentaire pour le vendeur (ex: instructions spéciales, personnalisation, etc.)"
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                rows="4"
              />
            </div>
            <div className="panier-summary-section">
              <h3>Adresse de facturation</h3>
              <div>
                <label className="billing-checkbox-label">
                  <input
                    type="checkbox"
                    checked={useDifferentBillingAddress}
                    onChange={(e) =>
                      setUseDifferentBillingAddress(e.target.checked)
                    }
                    className="billing-checkbox"
                  />
                  <span>Adresse de facturation différente ?</span>
                </label>
              </div>
              {useDifferentBillingAddress && (
                <div className="billing-address-form">
                  <input
                    type="text"
                    placeholder="Nom"
                    value={billingName}
                    onChange={(e) => setBillingName(e.target.value)}
                    className="form-input"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Prénom"
                    value={billingLastName}
                    onChange={(e) => setBillingLastName(e.target.value)}
                    className="form-input"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Adresse ligne 1"
                    value={billingAddress.address_line_1}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        address_line_1: e.target.value,
                      })
                    }
                    className="form-input"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Adresse ligne 2 (optionnel)"
                    value={billingAddress.address_line_2}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        address_line_2: e.target.value,
                      })
                    }
                    className="form-input"
                  />
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Code postal"
                      value={billingAddress.postal_code}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          postal_code: e.target.value,
                        })
                      }
                      className="form-input flex-1"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Ville"
                      value={billingAddress.admin_area_2}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          admin_area_2: e.target.value,
                        })
                      }
                      className="form-input flex-2"
                      required
                    />
                  </div>
                  <select
                    value={billingAddress.country_code}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        country_code: e.target.value,
                      })
                    }
                    className="form-input"
                    required
                  >
                    <option value="FR">France</option>
                    <option value="BE">Belgique</option>
                    <option value="CH">Suisse</option>
                    <option value="LU">Luxembourg</option>
                    <option value="ES">Espagne</option>
                    <option value="IT">Italie</option>
                    <option value="DE">Allemagne</option>
                  </select>
                  <input
                    type="tel"
                    placeholder="Téléphone"
                    value={billingPhone}
                    onChange={(e) => setBillingPhone(e.target.value)}
                    className="form-input"
                  />
                </div>
              )}
            </div>
            <div className="panier-summary-total">
              <span>Total</span>
              <span>{(getTotal() + getShippingCost()).toFixed(2)} €</span>
            </div>
            <div style={{ marginTop: "2rem" }}>
              {paypalClientId ? (
                <PayPalScriptProvider
                  options={{
                    "client-id": paypalClientId,
                    currency: "EUR",
                  }}
                >
                  <PayPalButtonWrapper
                  validateShippingForm={validateShippingForm}
                  getTotal={getTotal}
                  getShippingCost={getShippingCost}
                  shippingAddress={shippingAddress}
                  shippingPhone={shippingPhone}
                  shippingEmail={shippingEmail}
                  shippingName={shippingName}
                  shippingLastName={shippingLastName}
                  cart={cart}
                  useDifferentBillingAddress={useDifferentBillingAddress}
                  billingAddress={billingAddress}
                  billingPhone={billingPhone}
                  billingName={billingName}
                  billingLastName={billingLastName}
                  additionalInfo={additionalInfo}
                  clearCart={clearCart}
                  setShowSuccessNotification={setShowSuccessNotification}
                  shippingMethod={shippingMethod}
                />
              </PayPalScriptProvider>
              ) : (
                <p>Chargement de PayPal...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Panier;
